import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MODE_LABEL, SUPPLIER_MODES } from '@/components/features/planning/sidebar/supplier-orders-helpers'
import { addSupplierBrand, addSupplierEmailAddress, createSupplier, type SupplierOrderMode, type SupplierOverviewDto } from '@/lib/api/v2/planning'
import { ApiError } from '@/lib/api/client'
import { ownBrandSuggestion, sameBrand } from '@/lib/supplierDirectory'

const newSupplierSchema = z.object({
  name: z.string().trim().min(1, 'Le nom est requis.').max(200, '200 caractères au plus.'),
  allowedModes: z.array(z.enum(['Email', 'Portal', 'Tradeplace', 'Phone'])).min(1, 'Choisissez au moins un mode de commande.'),
})

interface NewSupplierDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (supplierId: string) => void
  /** The organization's suppliers — a duplicate name offers to open the existing card instead. */
  suppliers: SupplierOverviewDto[]
  onOpenExisting: (supplierId: string) => void
  /** Prefilled creation (« Mes ignorés » › Ajouter): the name to start from and the domain added on creation. */
  initialName?: string
  initialDomain?: string
}

/**
 * « Créer un fournisseur propose sa propre marque » — most suppliers supply their own brand, so the
 * name is proposed as a brand, checked; one click removes it for a wholesaler or a stonemason.
 */
export function NewSupplierDialog({ open, onOpenChange, onCreated, suppliers, onOpenExisting, initialName, initialDomain }: NewSupplierDialogProps) {
  const [name, setName] = useState(initialName ?? '')
  const [modes, setModes] = useState<SupplierOrderMode[]>(['Email'])
  const [withOwnBrand, setWithOwnBrand] = useState(true)
  // null = follow the name; a string = what the user typed (« Quooker » for « Quooker Benelux »).
  const [brandEdit, setBrandEdit] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [existing, setExisting] = useState<SupplierOverviewDto | null>(null)
  const [saving, setSaving] = useState(false)

  const brand = brandEdit !== null ? ownBrandSuggestion(brandEdit) : ownBrandSuggestion(name)

  const reset = () => {
    setName('')
    setModes(['Email'])
    setWithOwnBrand(true)
    setBrandEdit(null)
    setError(null)
    setExisting(null)
  }

  const save = async () => {
    const parsed = newSupplierSchema.safeParse({ name, allowedModes: modes })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Fournisseur invalide.')
      return
    }
    setSaving(true)
    try {
      // Same defaults as the « Commandé… » gesture: a new supplier is expected to confirm.
      const created = await createSupplier({ ...parsed.data, expectsConfirmation: true, defaultConfirmationDelayDays: null })
      if (initialDomain) {
        try {
          await addSupplierEmailAddress(created.id, initialDomain)
        } catch {
          toast.error(`Le fournisseur est créé, mais le domaine ${initialDomain} n'a pas pu être ajouté.`)
        }
      }
      if (withOwnBrand && brand) {
        try {
          await addSupplierBrand(created.id, brand)
        } catch {
          toast.error(`Le fournisseur est créé, mais la marque ${brand} n'a pas pu être ajoutée.`)
        }
      }
      reset()
      onOpenChange(false)
      onCreated(created.id)
    } catch (e) {
      // « Deux fournisseurs du même nom dans une organisation, impossible » — say it, and offer the card.
      const duplicate = e instanceof ApiError && e.code === 'SUPPLIER_ALREADY_EXISTS'
        ? suppliers.find((s) => sameBrand(s.name, parsed.data.name)) ?? null
        : null
      setExisting(duplicate)
      setError(
        duplicate
          ? `Un fournisseur ${duplicate.name} existe déjà`
          : e instanceof ApiError
            ? e.message
            : "Le fournisseur n'a pas pu être créé.",
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // While the supplier is being created the dialog stays: its completion would otherwise land
        // on, and wipe, a draft reopened in the meantime.
        if (!o && saving) return
        if (!o) reset()
        onOpenChange(o)
      }}
    >
      <DialogContent data-testid="new-supplier-dialog">
        <DialogHeader>
          <DialogTitle>Nouveau fournisseur</DialogTitle>
          <DialogDescription>
            {initialDomain ? `Avec le domaine ${initialDomain}. ` : ''}Délais, adresses et autres marques se règlent ensuite sur sa fiche.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="new-supplier-name">Nom</Label>
            <Input
              id="new-supplier-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setError(null)
                setExisting(null)
              }}
              autoFocus
              disabled={saving}
              data-testid="new-supplier-name-input"
            />
          </div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Commandé par</legend>
            <div className="flex flex-wrap gap-4">
              {SUPPLIER_MODES.map((m) => (
                <label key={m} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={modes.includes(m)}
                    onCheckedChange={(checked) => setModes((prev) => (checked ? [...prev, m] : prev.filter((x) => x !== m)))}
                    disabled={saving}
                    data-testid={`new-supplier-mode-${m.toLowerCase()}`}
                  />
                  {MODE_LABEL[m]}
                </label>
              ))}
            </div>
          </fieldset>
          {ownBrandSuggestion(name) && (
            <div className="flex items-center gap-2 text-sm">
              <Checkbox
                id="new-supplier-own-brand"
                checked={withOwnBrand}
                onCheckedChange={(c) => setWithOwnBrand(c === true)}
                disabled={saving}
                data-testid="new-supplier-own-brand-checkbox"
              />
              <label htmlFor="new-supplier-own-brand" className="shrink-0">
                Fournit la marque
              </label>
              <Input
                value={brandEdit ?? name}
                onChange={(e) => setBrandEdit(e.target.value)}
                disabled={!withOwnBrand || saving}
                className="h-8"
                aria-label="Marque fournie"
                data-testid="new-supplier-own-brand-input"
              />
            </div>
          )}
          {error && (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm text-destructive" data-testid="new-supplier-error">{error}</p>
              {existing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Closing from here bypasses the Dialog's onOpenChange: reset explicitly, or the
                    // next « Nouveau fournisseur » reopens on the old name and error.
                    const id = existing.id
                    reset()
                    onOpenExisting(id)
                  }}
                  data-testid="new-supplier-open-existing-btn"
                >
                  Ouvrir sa fiche
                </Button>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => void save()} disabled={saving} data-testid="new-supplier-save-btn">
            Créer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
